import React from 'react';
import styled from 'styled-components';
import { Body5, Body7 } from '@ui';

const Wrapper = styled.div`
  min-height: 28px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 3px;
  transition: background-color 0.15s ease;
  user-select: none;

  &:hover {
    background-color: ${({ theme }) => theme.themeColors.primaryLowOpacity};
  }
`;

const Arrow = styled(Body7)`
  color: ${({ theme }) => theme.themeColors.secondaryText || '#999'};
  transition: transform 0.15s ease;
  transform: ${({ $expanded }) => ($expanded ? 'rotate(90deg)' : 'rotate(0deg)')};
  display: inline-block;
  width: 12px;
`;

const Summary = styled(Body5)`
  color: ${({ theme }) => theme.themeColors.secondaryText || '#999'};
`;

const NestedContent = styled.div`
  padding: 6px 0 4px 4px;
  border-left: 2px solid ${({ theme }) => theme.themeColors.lowContrastBorder || '#e0e0e0'};
  margin-left: 8px;
  margin-top: 4px;
`;

const MAX_DEPTH = 50;

const getSummary = (value) => {
  if (Array.isArray(value)) {
    return `[${value.length} item${value.length !== 1 ? 's' : ''}]`;
  }
  if (typeof value === 'object' && value !== null) {
    const keyCount = Object.keys(value).length;
    return `{${keyCount} key${keyCount !== 1 ? 's' : ''}}`;
  }
  return '';
};

export const NestedCell = ({ value, path, onUpdate, depth, renderTable, collapseSignal }) => {
  const [localExpanded, setLocalExpanded] = React.useState(false);
  const prevVersionRef = React.useRef(collapseSignal?.version);

  React.useEffect(() => {
    if (collapseSignal && collapseSignal.version !== prevVersionRef.current) {
      prevVersionRef.current = collapseSignal.version;
      setLocalExpanded(!collapseSignal.collapsed);
    }
  }, [collapseSignal]);

  if (depth >= MAX_DEPTH) {
    return (
      <Wrapper>
        <Summary>{getSummary(value)} (max depth)</Summary>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <ToggleRow onClick={() => setLocalExpanded((prev) => !prev)}>
        <Arrow $expanded={localExpanded}>&#9654;</Arrow>
        <Summary>{getSummary(value)}</Summary>
      </ToggleRow>
      {localExpanded && <NestedContent>{renderTable(value, path, depth + 1)}</NestedContent>}
    </Wrapper>
  );
};
